import React from 'react';
import { Card, CardContent } from '../ui/card';
import { FileText, Mail, Phone, MapPin, AlertTriangle, Scale, Briefcase, CreditCard, Shield, Clock, Ban } from 'lucide-react';

export const TermsOfServicePage = () => {
  const lastUpdated = "February 2, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <FileText className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-600">Last Updated: {lastUpdated}</p>
        </div>

        {/* Introduction */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to <strong>Swift Move and Clean LLC</strong>. These Terms of Service ("Terms") govern your use of our website and the moving and cleaning services we provide. By accessing our website or using our services, you agree to be bound by these Terms.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Please read these Terms carefully before using our website or booking any services. If you do not agree with any part of these Terms, you may not use our website or services.
            </p>
          </CardContent>
        </Card>

        {/* Section 1: Services We Provide */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Briefcase className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">1. Services We Provide</h2>
            </div>
            
            <p className="text-gray-700 mb-4">Swift Move and Clean LLC offers the following services throughout Indiana:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Moving Services</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>Residential moving (local and long-distance within Indiana)</li>
              <li>Commercial and office moving</li>
              <li>Packing and unpacking services</li>
              <li>Furniture disassembly and assembly</li>
              <li>Loading and unloading assistance</li>
              <li>Moving supplies and materials</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Cleaning Services</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>Move-in and move-out cleaning</li>
              <li>Residential house cleaning</li>
              <li>Commercial and office cleaning</li>
              <li>Deep cleaning services</li>
              <li>Post-construction cleaning</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Website Features</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Online quote requests and booking</li>
              <li>Service scheduling and appointment management</li>
              <li>Customer communication through contact forms and chatbot</li>
              <li>Service information and pricing details</li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 2: User Responsibilities */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-green-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">2. User Responsibilities</h2>
            </div>
            
            <p className="text-gray-700 mb-4">When using our website and services, you agree to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Provide accurate and complete information when requesting quotes or booking services</li>
              <li>Be present or have an authorized representative present during scheduled services</li>
              <li>Ensure safe and legal access to the premises where services will be performed</li>
              <li>Disclose any items requiring special handling (fragile, valuable, hazardous)</li>
              <li>Secure or remove pets during moving and cleaning services</li>
              <li>Clear pathways and ensure adequate parking for our vehicles</li>
              <li>Notify us promptly of any changes to your service requirements</li>
              <li>Use our website only for lawful purposes</li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 3: Booking and Scheduling */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">3. Booking and Scheduling</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Quote Requests</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>Quotes provided through our website are estimates based on information you provide</li>
              <li>Final pricing may vary based on actual service requirements</li>
              <li>Quotes are valid for 30 days unless otherwise specified</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Service Confirmation</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>Bookings are confirmed upon receipt of deposit (where applicable) and written confirmation</li>
              <li>We will contact you to confirm service details before your scheduled appointment</li>
              <li>Service times are approximate; we will notify you of any significant delays</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Rescheduling</h3>
            <p className="text-gray-700">
              You may reschedule your service by contacting us at least 48 hours before your scheduled appointment at no additional charge. Rescheduling requests made less than 48 hours in advance may be subject to a rescheduling fee.
            </p>
          </CardContent>
        </Card>

        {/* Section 4: Payment Terms */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <CreditCard className="h-6 w-6 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">4. Payment Terms</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Pricing</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>All prices are quoted in US Dollars</li>
              <li>Pricing is based on the scope of work as described at the time of booking</li>
              <li>Additional services requested on-site may incur extra charges</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Payment Methods</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>We accept cash, credit cards, debit cards, and approved digital payment methods</li>
              <li>Payment is due upon completion of services unless otherwise arranged</li>
              <li>For larger projects, a deposit may be required at the time of booking</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Late Payment</h3>
            <p className="text-gray-700">
              Invoices not paid within 30 days of the service date may be subject to late fees of 1.5% per month. We reserve the right to pursue collection of unpaid balances through appropriate legal means.
            </p>
          </CardContent>
        </Card>

        {/* Section 5: Cancellation Policy */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-red-100 p-3 rounded-lg">
                <Ban className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">5. Cancellation Policy</h2>
            </div>
            
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>72+ hours notice:</strong> Full refund of any deposit paid</li>
              <li><strong>48-72 hours notice:</strong> 50% of deposit retained as cancellation fee</li>
              <li><strong>Less than 48 hours notice:</strong> Full deposit retained as cancellation fee</li>
              <li><strong>No-show:</strong> Full service charge may apply</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To cancel or reschedule, please contact us by phone at (812) 669-4165 or email at info@swiftmoveandclean.com.
            </p>
          </CardContent>
        </Card>

        {/* Section 6: Liability and Insurance */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-teal-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-teal-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">6. Liability and Insurance</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Our Coverage</h3>
            <p className="text-gray-700 mb-4">
              Swift Move and Clean LLC is fully licensed and insured. We maintain:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>Commercial General Liability Insurance ($1,000,000 per occurrence)</li>
              <li>General Aggregate Coverage ($2,000,000)</li>
              <li>Workers' Compensation Insurance</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Damage Claims</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li>Any damage claims must be reported within 24 hours of service completion</li>
              <li>We will investigate all claims promptly and fairly</li>
              <li>Pre-existing damage must be documented before services begin</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Limitations</h3>
            <p className="text-gray-700">
              We are not liable for damage to items not properly disclosed, items packed by the customer, or damage caused by factors outside our control (e.g., weather, building conditions).
            </p>
          </CardContent>
        </Card>

        {/* Section 7: Intellectual Property */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of Swift Move and Clean LLC and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-700">
              You may not reproduce, distribute, modify, or create derivative works from any content on this website without our prior written consent.
            </p>
          </CardContent>
        </Card>

        {/* Section 8: Disclaimers */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-orange-100 p-3 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">8. Disclaimers</h2>
            </div>
            
            <p className="text-gray-700 mb-4">
              <strong>Website "As Is":</strong> This website is provided on an "as is" and "as available" basis. We make no warranties, express or implied, regarding the website's operation, accuracy, or availability.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Service Estimates:</strong> Time and cost estimates are approximate and may vary based on actual conditions encountered during service.
            </p>
            <p className="text-gray-700">
              <strong>Third-Party Links:</strong> Our website may contain links to third-party websites. We are not responsible for the content or privacy practices of these sites.
            </p>
          </CardContent>
        </Card>

        {/* Section 9: Limitation of Liability */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-gray-100 p-3 rounded-lg">
                <Scale className="h-6 w-6 text-gray-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">9. Limitation of Liability</h2>
            </div>
            
            <p className="text-gray-700 mb-4">
              To the maximum extent permitted by law, Swift Move and Clean LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Your use or inability to use our website or services</li>
              <li>Any errors or omissions in website content</li>
              <li>Unauthorized access to your personal information</li>
              <li>Any third-party conduct or content</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Our total liability for any claim arising from these Terms or our services shall not exceed the amount paid by you for the specific service giving rise to the claim.
            </p>
          </CardContent>
        </Card>

        {/* Section 10: Governing Law */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of Indiana, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-700">
              Any disputes arising from these Terms or your use of our services shall be resolved in the courts of Bartholomew County, Indiana, and you consent to the exclusive jurisdiction of such courts.
            </p>
          </CardContent>
        </Card>

        {/* Section 11: Changes to Terms */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-700">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our website or services after any changes constitutes acceptance of the modified Terms. We encourage you to review these Terms periodically.
            </p>
          </CardContent>
        </Card>

        {/* Section 12: Contact Us */}
        <Card className="mb-8 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-orange-50">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">12. Contact Us</h2>
            <p className="text-gray-700 mb-6">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-blue-600 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Email</p>
                  <a href="mailto:info@swiftmoveandclean.com" className="text-blue-600 hover:underline">
                    info@swiftmoveandclean.com
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-blue-600 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Phone</p>
                  <a href="tel:8126694165" className="text-blue-600 hover:underline">
                    (812) 669-4165
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3 md:col-span-2">
                <MapPin className="h-5 w-5 text-blue-600 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Mailing Address</p>
                  <p className="text-gray-700">
                    Swift Move and Clean LLC<br />
                    130 West Brunswick Drive<br />
                    Columbus, IN 47201
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
